import { isValidURL } from "@utils/helpers";
import { FileUpload } from "primereact/fileupload";
import { OverlayPanel } from "primereact/overlaypanel";
import React, { forwardRef, useRef, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { SmallSpinner } from "shared/components/spinners/Spinner";
import { FlexColumnGap, GenericButton, IconDispatcher, Input, Label } from "shared/ui-basics/index";
import styled from "styled-components/macro";

const LinkForm = forwardRef(({ item, upsertAttachItem }, ref) => {

    const { t } = useTranslation();
    const [objectToCreate, setCreateObject] = useState(item);
    const checkIfValid = () => {
        if (objectToCreate?.description.isEmptyString() || !isValidURL(objectToCreate?.url))
            return false;
        return true;
    }

    const validated = checkIfValid();
    const handleChange = (e) => {
        let { name, value } = e.target;
        setCreateObject({ ...objectToCreate, [name]: value });
    };
    const save = (e) => {
        const copyObject = {
            ...objectToCreate,
            description: objectToCreate?.description.truncate(100, true),
            url: objectToCreate?.url
        }
        upsertAttachItem({ newObject: copyObject })
        ref.current.toggle(e);

    }
    return <FlexColumnGap gap='24'>
        <div>
            <Label className="pb-2">{t("LINK_NAME")}</Label>
            <Input
                variant="default"
                name="description"
                type="text"
                value={objectToCreate?.description}
                onChange={handleChange}
            />

        </div>
        <div>
            <Label className="pb-2">{t("LINK_URL")}</Label>
            <Input
                variant="default"
                name="url"
                type="text"
                placeholder="https://website.com"
                value={objectToCreate?.url}
                onChange={handleChange}
            />
        </div>
        <GenericButton variant="dark" onClick={save} disabled={!validated} >
            {t("SAVE")}
        </GenericButton>
    </FlexColumnGap>
});
LinkForm.displayName = "LinkForm";

const UploadFile = forwardRef(({ item, upsertAttachItem }, ref) => {

    const { t } = useTranslation();
    const [typeMsg, setTypeMsg] = useState("info");
    const [loading, setLoading] = useState(false);
    const fileUploadRef = useRef(null);
    const subscriptionPlan = useSelector((state) => state.auth?.userInfo?.UserSubscriptionPlanName);
    const isSubscriptionAllowed = ["Enterprise"].includes(subscriptionPlan);
    const subscriptionDetails = useSelector(({ settings }) => settings.subscriptionDetails);

    const uploadHandler = async (event) => {
        const file = event.files[0];

        if (file?.size > 10485760)//10 MB = 10,485,760 Bytes
        {
            setTypeMsg("error");
            fileUploadRef?.current.clear();
        }
        else {
            //set spinner
            setLoading(true);

            const newObject = {
                ...item,
                fileName: (event.files[0].name),
                description: (event.files[0].name),
                uploadFile: event.files[0]
            }
            upsertAttachItem({ newObject: newObject, onSuccess: (e) => { ref.current.toggle(e); } });
        }
    }
    const redirect = () => {
        if (subscriptionDetails?.upgradeButtonLink?.isEmptyString()) return window.open("https://www.cemantica.com/Pricing");

            const url = subscriptionDetails?.upgradeButtonLink?.split("'")[1];
            return window.open(url)

    }
    if (!isSubscriptionAllowed)
        return <FlexColumnGap gap={24}>
            <Label size={14} color="#262626">
                {t("UPGRADE_DEC1")}
            </Label>
            <Label size={14} color="#262626">
                {t("UPGRADE_DEC2")}
            </Label>
            <GenericButton variant="primary" onClick={redirect} >
                {t("UPGRADE_SUBSCRIPTION")}
            </GenericButton>
        </FlexColumnGap>

    const chooseOptions = { icon: IconDispatcher("UPLOAD", "pe-2"), label: t("UPLOAD_FILE"), className: "custom-choose-btn-block " };

    return <FlexColumnGap gap={24}>
        {loading && <GenericButton variant="dark" disabled><SmallSpinner /></GenericButton>}
        {!loading && <FileUpload ref={fileUploadRef} chooseOptions={chooseOptions}
            auto mode="basic" name="demo[]" customUpload uploadHandler={uploadHandler} />}
        {typeMsg === "info" &&
            <WrapperInfo type={typeMsg}>
                <div>{IconDispatcher("circle-info", "", { style: "solid", color: "#595959" })}</div>
                <Label size={12} color="#595959">{t("INFO_MAX_SIZE")}</Label>
            </WrapperInfo>
        }
        {typeMsg === "error" &&
            <WrapperInfo type={typeMsg}>
                <div>{IconDispatcher("triangle-exclamation", "", { style: "solid", color: "#D50000" })}</div>
                <Label size={12} color="#D50000">{t("ERROR_MAX_SIZE")}</Label>
            </WrapperInfo>
        }
    </FlexColumnGap>
});
UploadFile.displayName = "UploadFile";

const AttachCreatePopOver = ({ item, upsertAttachItem, innerRef,width = 270}) => {
    const { t } = useTranslation();
    const [key, setKey] = useState("link");
    return (
        <div className="position-relative">
            <OverlayPanelWrapper id="overlay_panel" ref={innerRef} showCloseIcon width={width}>
                <Tabs activeKey={key} onSelect={(k) => setKey(k)} className="m-3 mb-4" mountOnEnter={true} unmountOnExit={true}>
                    <Tab eventKey="link" className="m-3" title={t("LINK")}>
                        <LinkForm item={item} upsertAttachItem={upsertAttachItem} ref={innerRef} />
                    </Tab>
                    <Tab eventKey="upload" className="m-3" title={t("JS_26")}>
                        <UploadFile item={item} upsertAttachItem={upsertAttachItem} ref={innerRef} />
                    </Tab>
                </Tabs>

            </OverlayPanelWrapper>
        </div>
    );
};


AttachCreatePopOver.displayName = "AttachCreatePopOver";
export default AttachCreatePopOver;

const OverlayPanelWrapper = styled(OverlayPanel)`
	width: ${(props) => props.width}px;
	max-height: 800px !important;
	z-index: 1060 !important;
    box-shadow: 1px 8px 16px 0px rgba(0, 0, 0, 0.15);
	.p-overlaypanel-content {
		padding: 0px;
	}
	.p-overlaypanel-close {
		display: none;
	}
    &::before,
	&::after {
		display: none;
	}
`;

const WrapperInfo = styled.div`
    /* margin-bottom:1rem; */
    display: flex;
    align-items: flex-start;
    ${(props) => props.type === "info" && `
        background: #F5F5F5;
    `}
    ${(props) => props.type === "error" && `
        background: #FFEDED;
    `}
    padding:4px;
    border-radius:4px;
    gap:8px;

 `